import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { BASE_URL } from "../../config";
import { getAuthHeaders } from "../../services/useAuth";
import CustomButton from "../../pages/CustomButton";
import Loader from "../../dist/Loader";

const toISODate = (d) => {
  const dd = new Date(d);
  if (isNaN(dd)) return "";
  const yyyy = dd.getFullYear();
  const mm = String(dd.getMonth() + 1).padStart(2, "0");
  const day = String(dd.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${day}`;
};

const eachDate = (start, end) => {
  const s = new Date(start);
  const e = new Date(end);
  const out = [];
  if (isNaN(s) || isNaN(e) || s > e) return out;
  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    out.push(new Date(d));
  }
  return out;
};

// Mapping function for the API response
const mapRow = (raw) => ({
  emp_code: raw.emp_code || "-",
  employee: raw.employee || "-",
  state: raw.state || "-",
  doj: raw.doj || "-",
  manager: raw.manager || "-",
  designation: raw.designation || "-",
  division: raw.division || "-",
  headquarter: raw.headquarter || "-",
  working_date: raw.working_date || "-",
  retailer_name: raw.retailer_name || "-",
  product_code: raw.product_code || "-",
  product_name: raw.product_name || "-",
  // keep property names consistent: use `quantity` not `qty`
  quantity: raw.quantity ?? 0,
  rate: raw.rate ?? 0,
  amount: raw.amount ?? 0,
  booked_by_employee: raw.booked_by_employee ?? 0,
});

const StateWiseProduct = () => {
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([{ id: "All", name: "All" }]);
  const [substates, setSubstates] = useState([{ id: "All", name: "All" }]);

  // Filters
  const [selectedState, setSelectedState] = useState("");
  const [selectedSubState, setSelectedSubState] = useState("All");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [schemeOn, setSchemeOn] = useState("0");
  const [employees, setEmployees] = useState([
    { id: "All", first_name: "All", last_name: "" },
  ]);
  const [selectedEmployee, setSelectedEmployee] = useState("All");

  // Dates
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useState(toISODate(firstOfMonth));
  const [endDate, setEndDate] = useState(toISODate(today));

  // Results
  const [tableData, setTableData] = useState([]);
  const [showContainers, setShowContainers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const dateList = useMemo(
    () => eachDate(startDate, endDate),
    [startDate, endDate]
  );

  const totals = useMemo(() => {
    if (!tableData || tableData.length === 0) {
      return { totalQty: 0, totalBooked: 0, totalAmount: 0 };
    }
    return tableData.reduce(
      (acc, row) => {
        const qty = Number(row.quantity) || 0;
        const booked = Number(row.booked_by_employee) || 0;
        const amt = Number(row.amount) || 0;
        acc.totalQty += qty;
        acc.totalBooked += booked;
        acc.totalAmount += amt;
        return acc;
      },
      { totalQty: 0, totalBooked: 0, totalAmount: 0 }
    );
  }, [tableData]);

  // Load states once
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders(),
        });
        setStates(res.data || []);
      } catch (err) {
        console.error(err);
        setError("Failed to load states");
      }
    };
    fetchStates();
  }, []);

  // Load divisions when selectedState changes
  useEffect(() => {
    if (!selectedState) {
      setDivisions([{ id: "All", name: "All" }]);
      setSelectedDivision("All");
      return;
    }
    const fetchDivisions = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/divisions?state_id=${selectedState}`,
          { headers: getAuthHeaders() }
        );
        const list = res.data || [];
        setDivisions(list);
        setSelectedDivision(list.length ? list[0].id : "All");
      } catch (err) {
        console.error("divisions load failed", err);
        setError("Failed to load divisions");
      }
    };
    fetchDivisions();
  }, [selectedState]);

  // Load substates when selectedState changes
  useEffect(() => {
    if (!selectedState) {
      setSubstates([{ id: "All", name: "All" }]);
      return;
    }
    const fetchSubstates = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/substates?state_id=${selectedState}`,
          { headers: getAuthHeaders() }
        );
        const list = res.data || [];
        setSubstates([{ id: "All", name: "All" }, ...list]);
        setSelectedSubState("All");
      } catch (err) {
        console.error("substates load failed", err);
        setError("Failed to load substates");
      }
    };
    fetchSubstates();
  }, [selectedState]);

  // Fetch employees based only on state and substate
  const fetchEmployees = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedState) params.set("state_id", selectedState);
      if (selectedSubState && selectedSubState !== "All")
        params.set("substate_id", selectedSubState);

      const res = await axios.get(
        `${BASE_URL}/api/employees?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      const list = Array.isArray(res.data) ? res.data : res.data?.data || [];

      const normalized = list.map((e, i) => ({
        id: e.id ?? e.employee_id ?? e.emp_id ?? String(i + 1),
        first_name: e.first_name ?? e.employee_first_name ?? e.name ?? "",
        last_name: e.last_name ?? e.employee_last_name ?? "",
        designation: e.designation ?? "",
        headquarter_name: e.headquarter_name ?? e.hq ?? e.city_hq ?? "",
        division: e.division ?? e.division_name ?? "",
        state_name: e.state_name ?? e.state ?? "",
      }));

      setEmployees([
        { id: "All", first_name: "All", last_name: "" },
        ...normalized,
      ]);
      setSelectedEmployee("All");
    } catch (err) {
      console.error("employees load failed", err);
      setEmployees([{ id: "All", first_name: "All", last_name: "" }]);
      setSelectedEmployee("All");
    }
  }, [selectedState, selectedSubState]);

  useEffect(() => {
    fetchEmployees();
  }, [selectedState, selectedSubState, fetchEmployees]);

  const handleGoClick = async () => {
    if (!selectedState) {
      setError("Please select a state");
      return;
    }
    if (!startDate || !endDate) {
      setError("Please select start and end date");
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError("Start date must be before end date");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.set("state_id", selectedState);
      if (selectedSubState && selectedSubState !== "All")
        params.set("substate_id", selectedSubState);
      if (selectedDivision && selectedDivision !== "All") {
        const divObj = divisions.find(
          (d) => String(d.id) === String(selectedDivision)
        );
        if (divObj?.name) params.set("division", divObj.name);
      }

      if (selectedEmployee && selectedEmployee !== "All") {
        params.set("employee_id", selectedEmployee);
      }
      params.set("start_date", startDate);
      params.set("end_date", endDate);

      const url = `${BASE_URL}/api/reports/state-wise-product?${params.toString()}`;
      const res = await axios.get(url, { headers: getAuthHeaders() });

      const payload = res.data?.products || [];
      setTableData(payload.map(mapRow));
      setShowContainers(true);
    } catch (err) {
      console.error("ff-product-wise call failed", err);
      setError("Failed to fetch data");
      setTableData([]);
      setShowContainers(true);
    } finally {
      setIsLoading(false);
    }
  };

  /* ------------------ Print and Export helpers ------------------ */

  const buildHeaders = () => [
    "S.No",
    "Emp ID",
    "Employee",
    "State",
    "DOJ",
    "Manager",
    "Designation",
    "Division",
    "HQ",
    "Working Date",
    "Retailer",
    "Product Code",
    "Product",
    "Qty",
    "Rate",
    "Amount",
  ];

  const formatForCsv = (row) => {
    const r = mapRow(row);
    return [
      r.emp_code,
      r.emp_code,
      r.employee,
      r.state,
      r.doj,
      r.manager,
      r.designation,
      r.division,
      r.headquarter,
      r.working_date,
      r.retailer_name,
      r.product_code,
      r.product_name,
      String(r.quantity ?? 0),
      (Number(r.rate) ?? 0).toFixed(2),
      (Number(r.amount) ?? 0).toFixed(2),
    ];
  };

  const escapeCsv = (val) => {
    if (val === null || val === undefined) return "";
    const s = String(val);
    if (/[,"\n]/.test(s)) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const downloadCSV = () => {
    if (!tableData || tableData.length === 0) return;
    const rows = [buildHeaders(), ...tableData.map(formatForCsv)];
    const csv = rows
      .map((r) => r.map((c) => escapeCsv(c)).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const dateSuffix = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const filename = `ff-product-wise-${dateSuffix}.csv`;
    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, filename);
      return;
    }
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const escapeHtml = (unsafe) => {
    if (unsafe === null || unsafe === undefined) return "";
    return String(unsafe)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const handlePrint = () => {
    if (!tableData || tableData.length === 0) return;
    const stateName = states.find(
      (s) => String(s.id) === String(selectedState)
    )?.name;
    const divisionName =
      selectedDivision !== "All"
        ? divisions.find((d) => String(d.id) === String(selectedDivision))?.name
        : "All";
    const employeeLabel = selectedEmployee === "All" ? "All" : selectedEmployee;

    const headerHtml = `<div style="font-family: Arial, sans-serif; margin-bottom: 8px;">
      <h3 style="margin:0"> Product Wise Report</h3>
      <div style="font-size:13px; color:#444; margin-top:6px;">
        <strong>State:</strong> ${stateName || ""} &nbsp; | &nbsp;
        <strong>Division:</strong> ${divisionName} &nbsp; | &nbsp;
        <strong>Employee:</strong> ${employeeLabel} &nbsp; | &nbsp;
        <strong>From:</strong> ${startDate} &nbsp; <strong>To:</strong> ${endDate}
      </div>
    </div>`;

    const style = `
      <style>
        table { width:100%; border-collapse: collapse; font-family: Arial, sans-serif; font-size: 11px;}
        th, td { border: 1px solid #ddd; padding: 6px; text-align: left; vertical-align: top; }
        th { background: #f0f9ff; font-weight: 600; }
        .text-right { text-align: right; }
      </style>
    `;

    const rowsHtml = tableData
      .map((row, idx) => {
        const r = mapRow(row);
        return `<tr>
      <td style="text-align:center;">${idx + 1}</td>
      <td>${escapeHtml(r.state)}</td>
      <td>${escapeHtml(r.division)}</td>
      <td>${escapeHtml(r.product_name)}</td>
      <td style="text-align:right;">${escapeHtml(r.quantity)}</td>
      <td style="text-align:right;">${escapeHtml(r.booked_by_employee)}</td>
    </tr>`;
      })
      .join("");

    const tableHtml = `
  <table>
    <thead>
      <tr>
        <th style="padding:6px; border:1px solid #ddd; text-align:center;">S.No</th>
        <th style="padding:6px; border:1px solid #ddd; text-align:left;">State</th>
        <th style="padding:6px; border:1px solid #ddd; text-align:left;">Division</th>
        <th style="padding:6px; border:1px solid #ddd; text-align:left;">Product</th>
        <th style="padding:6px; border:1px solid #ddd; text-align:right;">Quantity</th>
        <th style="padding:6px; border:1px solid #ddd; text-align:right;">Booked By Employee</th>
      </tr>
    </thead>
    <tbody>
      ${rowsHtml}
    </tbody>
    <tfoot>
      <tr>
        <td colspan="3" style="padding:6px; border:1px solid #ddd; text-align:right; font-weight:600;">Totals</td>
        <td style="padding:6px; border:1px solid #ddd; text-align:right; font-weight:600;">-</td>
        <td style="padding:6px; border:1px solid #ddd; text-align:right; font-weight:600;">${totals.totalQty}</td>
        <td style="padding:6px; border:1px solid #ddd; text-align:right; font-weight:600;">${totals.totalBooked}</td>
      </tr>
    </tfoot>
  </table>
`;

    const printWindow = window.open("", "_blank", "width=1000,height=800");
    if (!printWindow) {
      alert("Unable to open print window. Please allow popups for this site.");
      return;
    }
    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>FF Product Wise Report</title>
          ${style}
        </head>
        <body>
          ${headerHtml}
          ${tableHtml}
        </body>
      </html>
    `);
    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  return (
    <div className="p-2  border-2 border-gray-300 rounded-lg bg-white">
      {/* Filters container */}
      <div className=" border-2 border-gray-300 rounded-sm bg-white p-3">
        <div className="max-w-6xl mx-auto">
          {/* Heading */}
          <h2 className="text-2xl font-bold flex justify-center  gap-2">
            <span className="text-blue-700">State</span>
            <span className="text-blue-500">Tempation</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            {/* Left side: State + Dates */}
            <div className="space-y-2">
              {/* State */}
              <div className="flex items-center">
                <label className="w-24 font-medium text-gray-800">State</label>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="flex-1 border border-gray-400 rounded-sm h-8 px-2 focus:outline-none focus:ring-1 focus:ring-blue-300"
                >
                  <option value="">Select State</option>
                  {states.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Dates */}
              <div className="flex items-center gap-3">
                <label className="w-24 font-medium text-gray-800">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border border-gray-400 rounded-sm h-8 px-2 w-36 focus:outline-none focus:ring-1 focus:ring-blue-300"
                />
                <label className="font-medium text-gray-800">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border border-gray-400 rounded-sm h-8 px-2 w-36 focus:outline-none focus:ring-1 focus:ring-blue-300"
                />
              </div>
            </div>

            <div className="space-y-2">
              {/* Division */}
              <div className="flex items-center">
                <label className="w-24 font-medium text-gray-800">
                  Division
                </label>
                <select
                  value={selectedDivision}
                  onChange={(e) => setSelectedDivision(e.target.value)}
                  disabled={!selectedState}
                  className="flex-1 border border-gray-400 rounded-sm h-8 px-2 bg-white disabled:opacity-60 focus:outline-none focus:ring-1 focus:ring-blue-300"
                >
                  <option value="">All</option>
                  {divisions.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-center mt-2">
                <CustomButton
                  onClick={handleGoClick}
                  variant="go"
                  disabled={isLoading}
                >
                  {isLoading ? <Loader /> : "Go"}
                </CustomButton>
              </div>
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-600 mt-2 text-center">{error}</div>
          )}
        </div>
      </div>

      {showContainers && (
        <div className="border rounded shadow  table-wrapper mt-5">
          <table className="unlock-dcr-leave-table text-sm ">
            <thead className="bg-blue-500 text-white">
              <tr>
                <th className="border px-1 py-1">S.No</th>
                <th className="border px-1 py-1">State</th>
                <th className="border px-1 py-1">Division</th>
                <th className="border px-1 py-1">Product</th>
                <th className="border px-1 py-1 text-right">Quantity</th>
                <th className="border px-1 py-1 text-right">
                  Booked By Employee
                </th>
              </tr>
            </thead>

            <tbody>
              {tableData && tableData.length > 0 ? (
                tableData.map((r, idx) => (
                  <tr
                    key={idx}
                    className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="border px-1 py-1 text-center">{idx + 1}</td>
                    <td className="border px-1 py-1">{r.state}</td>
                    <td className="border px-1 py-1">{r.division}</td>

                    <td
                      className="border px-1 py-1 whitespace-normal break-words max-w-[200px]"
                      title={r.product_name}
                    >
                      {r.product_name}
                    </td>

                    <td className="border px-1 py-1 text-right">
                      {r.quantity}
                    </td>

                    <td className="border px-1 py-1 text-right">
                      {r.booked_by_employee}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="border px-2 py-8 text-center" colSpan={6}>
                    No data found
                  </td>
                </tr>
              )}
            </tbody>

            <tfoot className="bg-blue-600 font-semibold text-white">
              <tr>
                <td className="border px-1 py-1 text-center" colSpan={3}>
                  Total
                </td>

                <td className="border px-1 py-1 text-right">-</td>

                <td className="border px-1 py-1 text-right">
                  {totals.totalQty}
                </td>

                <td className="border px-1 py-1 text-right">
                  {totals.totalBooked}
                </td>
              </tr>
            </tfoot>
          </table>
          <div className="flex gap-3 justify-center mt-4">
            <CustomButton
              variant="print"
              label={"Print"}
              onClick={handlePrint}
              disabled={!tableData || tableData.length === 0}
            >
              🖨
            </CustomButton>
            <CustomButton
              variant="export"
              label={"Export"}
              onClick={downloadCSV}
              disabled={!tableData || tableData.length === 0}
            >
              📥
            </CustomButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default StateWiseProduct;
